import { AppLink } from "../app-link.js";
import type { LucideIcon } from "../icons.js";

/** One row of the rail (DESIGN.md §5, "Rows"). */
export interface SideRow {
  readonly href: string;
  readonly label: string;
  readonly icon: LucideIcon;
  /**
   * Whether this row is the screen on show. A function rather than a path,
   * because a row can stand for more than one address: Fan-out is its studies
   * too, and Find questions is both of the ways to find them.
   */
  readonly isHere: (path: string) => boolean;
}

/** A group of rows, under a label or, for the brand's own views, under the brand. */
export function SideNav({
  label,
  name,
  rows,
  path,
}: {
  /** Printed over the rows. The brand's rows have none: the brand is their heading. */
  readonly label: string | null;
  /** What a screen reader calls the group. */
  readonly name: string;
  readonly rows: readonly SideRow[];
  readonly path: string;
}) {
  return (
    <div className="side-group">
      {label === null ? null : (
        <p className="side-label" aria-hidden="true">
          {label}
        </p>
      )}
      <nav className="side-nav" aria-label={name}>
        {rows.map((row) => {
          const here = row.isHere(path);
          return (
            <AppLink
              key={row.label}
              href={row.href}
              className="side-link"
              aria-current={here ? "page" : undefined}
            >
              <row.icon className="side-link-icon" aria-hidden="true" />
              <span className="side-link-label">{row.label}</span>
            </AppLink>
          );
        })}
      </nav>
    </div>
  );
}

/** A row for exactly one address, or that address and anything under it. */
export function at(...paths: readonly string[]): (path: string) => boolean {
  return (path) => paths.some((one) => path === one || path.startsWith(`${one}/`));
}
